import { scaleQuantile, scaleLinear } from "d3-scale";

export const PALETTES: Record<string, [number, number, number][]> = {
  // Loukas Notebook Style: blue (low) -> cyan -> yellow -> orange -> red (high)
  loukas: [
    [0, 0, 255],    // Blue
    [0, 255, 255],  // Cyan
    [255, 255, 0],  // Yellow
    [255, 165, 0],  // Orange
    [255, 0, 0]     // Red
  ],
  blues: [
    [239, 243, 255], [189, 215, 231], [107, 174, 214], [49, 130, 189], [8, 81, 156],
  ],
  reds: [
    [254, 229, 217], [252, 174, 145], [251, 106, 74], [222, 45, 38], [165, 15, 21],
  ],
  greens: [
    [237, 248, 233], [186, 228, 179], [116, 196, 118], [49, 163, 84], [0, 109, 44],
  ],
  default: [[50, 150, 250]],
};

export interface LayerStyle {
  type: "geojson" | "h3_cells" | "comparison";
  opacity?: number;
  filled?: boolean;
  stroked?: boolean;
  lineWidth?: number;
  lineColor?: [number, number, number];
  fillColor?: | [number, number, number, number?] | { field: string; scale: "quantile" | "linear"; palette: string; }; 
}

export function createColorScale(
  features: any[],
  style?: LayerStyle
): (feature: any) => [number, number, number, number] {
  
  const defaultColor: [number, number, number, number] = [50, 150, 250, 150];
  if (!style || !style.fillColor) return () => defaultColor;
  if (Array.isArray(style.fillColor)) return () => style.fillColor as [number, number, number, number];

  const config = style.fillColor;
  const field = config.field;
  const paletteName = config.palette || "blues";
  const range = PALETTES[paletteName] || PALETTES["default"];
  const opacity = (style.opacity || 0.8) * 255;

  // Filter out Zeros/NaN for Domain Calculation to ensure color contrast in data
  const values = features
    .map((f) => f.properties?.[field])
    .filter((v) => typeof v === "number" && !isNaN(v) && v > 0); 
  
  // Fallback if everything is 0 or no features found
  if (values.length === 0) return () => [0, 0, 0, 0];

  let scale: any;
  if (config.scale === "linear") {
    const min = Math.min(...values);
    const max = Math.max(...values);
    scale = scaleLinear().domain([min, max]).range([0, range.length - 1]); 
  } else {
    scale = scaleQuantile().domain(values).range(range);
  }

  return (feature: any) => {
    const val = feature.properties?.[field];
    if (val === undefined || val === null || isNaN(val)) return [0, 0, 0, 0];
    
    // Treat 0 as transparent (dry areas) to match Loukas notebook visual style
    if (val === 0) return [0, 0, 0, 0]; 

    let rgb;
    try {
        if (config.scale === "linear") {
            const index = Math.round(scale(val));
            rgb = range[Math.max(0, Math.min(index, range.length - 1))];
        } else {
            rgb = scale(val);
        }
    } catch (e) { return defaultColor; }
    
    if (!rgb || !Array.isArray(rgb)) return defaultColor;
    return [...rgb, opacity] as [number, number, number, number];
  };
}

export interface LegendItem { color: string; label: string; }

export function getLegendData(features: any[], style?: LayerStyle): { title: string; items: LegendItem[]; type: 'linear' | 'quantile' } | null {
  if (!style || !style.fillColor || Array.isArray(style.fillColor)) return null;

  const config = style.fillColor;
  const field = config.field;
  const range = PALETTES[config.palette || "blues"] || PALETTES["default"];
  
  const values = features
    .map((f) => f.properties?.[field])
    .filter((v) => typeof v === "number" && !isNaN(v) && v > 0);

  if (values.length === 0) return null;

  const items: LegendItem[] = [];
  const toCss = (rgb: number[]) => `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
  
  const maxVal = Math.max(...values);
  // Auto-precision: use 2 decimals for small values (probability), 0 for large (elevation)
  const maxFraction = maxVal > 10 ? 0 : 2;
  const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: maxFraction });

  const min = Math.min(...values);
  const max = maxVal;

  if (config.scale === "linear") {
    items.push({ color: toCss(range[0]), label: fmt(min) });
    items.push({ color: toCss(range[range.length - 1]), label: fmt(max) });
    return { title: field, items, type: 'linear' };
  } else {
    const scale = scaleQuantile().domain(values).range(range);
    const quantiles = scale.quantiles();
    
    // First Bucket
    items.push({ color: toCss(range[0]), label: `${fmt(min)} – ${fmt(quantiles[0])}` });
    
    // Middle Buckets
    for(let i=0; i < quantiles.length - 1; i++) {
        items.push({ color: toCss(range[i+1]), label: `${fmt(quantiles[i])} – ${fmt(quantiles[i+1])}` });
    }
    
    // Last Bucket
    items.push({ color: toCss(range[range.length - 1]), label: `${fmt(quantiles[quantiles.length - 1])} – ${fmt(max)}` });

    return { title: field, items, type: 'quantile' };
  }
}
